import React from 'react';
import { Dialog } from '../reusable';
import { getPoStatusPresentation } from './purchaseOrderSummary';

const formatMoney = (value, currency = 'CLP') => {
  const raw = String(currency || 'CLP').trim().toUpperCase() || 'CLP';
  const code = /^[A-Z]{3}$/.test(raw) ? raw : 'CLP';
  try {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: code,
      maximumFractionDigits: code === 'CLP' ? 0 : 2,
    }).format(Number(value || 0));
  } catch {
    return `${code} ${Number(value || 0).toLocaleString('es-CL')}`;
  }
};

const formatDate = (value) => {
  if (!value) return '—';
  const raw = String(value).trim();
  const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const date = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(raw);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const statusPresentation = (statusRaw, isDarkMode) => getPoStatusPresentation(statusRaw, isDarkMode);

const theme = (isDarkMode) =>
  isDarkMode
    ? {
        pageBg: '#18181b',
        cardBg: '#27272a',
        cardBorder: '#3f3f46',
        tableHead: '#1f1f23',
        text: '#f4f4f5',
        muted: '#a1a1aa',
      }
    : {
        pageBg: '#eef2f7',
        cardBg: '#ffffff',
        cardBorder: '#d8dee8',
        tableHead: '#f1f5f9',
        text: '#0f172a',
        muted: '#64748b',
      };

const Card = ({ t, children, style }) => (
  <div
    style={{
      marginBottom: 12,
      borderRadius: 10,
      border: `1px solid ${t.cardBorder}`,
      background: t.cardBg,
      padding: '14px 16px',
      ...style,
    }}
  >
    {children}
  </div>
);

const Field = ({ label, value, t, emphasize = false }) => (
  <div style={{ minWidth: 0, lineHeight: 1.2 }}>
    <div
      style={{
        fontSize: 11,
        fontWeight: 600,
        color: t.muted,
        marginBottom: 3,
      }}
    >
      {label}
    </div>
    <div
      style={{
        fontSize: emphasize ? 14 : 13,
        fontWeight: emphasize ? 700 : 600,
        color: t.text,
        wordBreak: 'break-word',
        whiteSpace: 'pre-wrap',
      }}
    >
      {value || '—'}
    </div>
  </div>
);

const NoteCard = ({ title, value, t }) => (
  <Card t={t} style={{ marginBottom: 10 }}>
    <div style={{ fontSize: 13, fontWeight: 700, color: t.text, marginBottom: 6 }}>{title}</div>
    <div
      style={{
        fontSize: 13,
        color: t.text,
        lineHeight: 1.4,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        minHeight: 28,
      }}
    >
      {value?.trim() ? value : '—'}
    </div>
  </Card>
);

const PurchaseOrderDetailModal = ({
  isOpen,
  onClose,
  purchaseOrder,
  isDarkMode = false,
  onEdit,
}) => {
  if (!purchaseOrder) return null;

  const t = theme(isDarkMode);
  const items = Array.isArray(purchaseOrder.items) ? purchaseOrder.items : [];
  const currency = String(purchaseOrder.currency || 'CLP').toUpperCase();
  const st = statusPresentation(purchaseOrder.status, isDarkMode);
  const advance = Number(purchaseOrder.advance_amount || 0);
  const withholdings = Number(purchaseOrder.withholdings_amount || 0);
  const amountDue =
    purchaseOrder.amount_due != null
      ? Number(purchaseOrder.amount_due)
      : Number(purchaseOrder.total_amount || 0) - advance - withholdings;
  const contactMeta = [purchaseOrder.supplier_contact_email, purchaseOrder.supplier_contact_phone]
    .filter(Boolean)
    .join(' · ');
  const contactValue = purchaseOrder.supplier_contact_name
    ? contactMeta
      ? `${purchaseOrder.supplier_contact_name}\n${contactMeta}`
      : purchaseOrder.supplier_contact_name
    : '';
  const conversion =
    purchaseOrder.exchange_rate != null
      ? Number(purchaseOrder.exchange_rate).toLocaleString('es-CL', { maximumFractionDigits: 6 })
      : '';

  const generalFields = [
    { label: 'Proveedor', value: purchaseOrder.supplier_name, emphasize: true },
    { label: 'Contacto', value: contactValue },
    { label: 'Ejecutivo', value: purchaseOrder.buyer_name || purchaseOrder.created_by },
    { label: 'RUT', value: purchaseOrder.supplier_rut },
    { label: 'Fecha', value: formatDate(purchaseOrder.order_date) },
    { label: 'Tipo de documento', value: purchaseOrder.document_type },
    { label: 'Moneda', value: currency },
    { label: 'Conversión', value: conversion },
    { label: 'Condición de pago', value: purchaseOrder.payment_terms },
    { label: 'Condiciones de entrega', value: purchaseOrder.delivery_conditions },
    { label: 'Lugar de entrega', value: purchaseOrder.delivery_place },
  ];

  const body = (
    <div
      style={{
        padding: '4px 4px 12px',
        overflowY: 'auto',
        maxHeight: 'calc(90vh - 140px)',
        background: t.pageBg,
      }}
    >
      <Card t={t}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: '12px 20px',
          }}
        >
          {generalFields.map((field) => (
            <Field
              key={field.label}
              label={field.label}
              value={field.value}
              t={t}
              emphasize={field.emphasize}
            />
          ))}
        </div>
      </Card>

      <div style={{ margin: '4px 2px 8px', fontSize: 14, fontWeight: 700, color: t.text }}>
        Ítems ({items.length})
      </div>

      <Card t={t} style={{ padding: 0, overflow: 'hidden' }}>
        {items.length === 0 ? (
          <p style={{ margin: 0, padding: 14, fontSize: 13, color: t.muted }}>Sin ítems.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
              <thead>
                <tr style={{ background: t.tableHead }}>
                  {['#', 'Tipo', 'Código', 'Item', 'Detalle', 'Cant.', 'Formato', 'Precio', 'Total'].map((h) => (
                    <th
                      key={h}
                      style={{
                        padding: '8px 12px',
                        textAlign: ['Cant.', 'Precio', 'Total', '#'].includes(h) ? 'right' : 'left',
                        fontSize: 12,
                        fontWeight: 600,
                        color: t.muted,
                        borderBottom: `1px solid ${t.cardBorder}`,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => (
                  <tr
                    key={item.id || `${item.item_code}-${item.description}-${index}`}
                    style={{ borderTop: index === 0 ? 'none' : `1px solid ${t.cardBorder}` }}
                  >
                    <td style={{ padding: '8px 12px', fontSize: 12, textAlign: 'right', color: t.muted }}>
                      {index + 1}
                    </td>
                    <td style={{ padding: '8px 12px', fontSize: 12, color: t.muted }}>{item.item_type || '—'}</td>
                    <td style={{ padding: '8px 12px', fontSize: 12, color: t.muted }}>{item.item_code || '—'}</td>
                    <td style={{ padding: '8px 12px', fontSize: 13, color: t.text, fontWeight: 500 }}>
                      {item.description || '—'}
                    </td>
                    <td style={{ padding: '8px 12px', fontSize: 12, color: t.muted }}>
                      {item.detail || '—'}
                    </td>
                    <td style={{ padding: '8px 12px', fontSize: 12, textAlign: 'right' }}>{item.quantity ?? 0}</td>
                    <td style={{ padding: '8px 12px', fontSize: 12 }}>{item.unit || 'UN'}</td>
                    <td style={{ padding: '8px 12px', fontSize: 12, textAlign: 'right' }}>
                      {formatMoney(item.unit_price, currency)}
                    </td>
                    <td style={{ padding: '8px 12px', fontSize: 12, textAlign: 'right', fontWeight: 700 }}>
                      {formatMoney(
                        item.line_total ?? Number(item.quantity || 0) * Number(item.unit_price || 0),
                        currency
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            padding: '12px 14px',
            borderTop: `1px solid ${t.cardBorder}`,
            background: isDarkMode ? '#1f1f23' : '#f8fafc',
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: 280,
              background: t.cardBg,
              border: `1px solid ${t.cardBorder}`,
              borderRadius: 10,
              padding: '10px 14px',
              boxShadow: isDarkMode ? 'none' : '0 1px 3px rgba(15,23,42,0.06)',
            }}
          >
            {[
              {
                label: `Subtotal (${currency})`,
                value: formatMoney(
                  purchaseOrder.subtotal_original != null
                    ? purchaseOrder.subtotal_original
                    : purchaseOrder.subtotal,
                  currency
                ),
              },
              { label: 'Total neto', value: formatMoney(purchaseOrder.subtotal, 'CLP') },
              { label: 'IVA', value: formatMoney(purchaseOrder.tax_amount, 'CLP') },
              {
                label: 'Total',
                value: formatMoney(purchaseOrder.total_amount, 'CLP'),
                dividerBefore: true,
                strong: true,
              },
              { label: 'Anticipo', value: formatMoney(advance, 'CLP') },
              { label: 'Retención', value: formatMoney(withholdings, 'CLP') },
              {
                label: 'A pagar',
                value: formatMoney(amountDue, 'CLP'),
                dividerBefore: true,
                strong: true,
              },
            ].map((row) => (
              <div key={row.label}>
                {row.dividerBefore ? (
                  <div
                    style={{
                      height: 1,
                      background: t.cardBorder,
                      margin: '8px 0',
                    }}
                  />
                ) : null}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    gap: 16,
                    padding: '4px 0',
                    fontSize: row.strong ? 14 : 13,
                    fontWeight: row.strong ? 700 : 500,
                    color: t.text,
                  }}
                >
                  <span style={{ color: row.strong ? t.text : t.muted }}>{row.label}</span>
                  <span>{row.value}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>

      <NoteCard title="Comentarios" value={purchaseOrder.comments} t={t} />
      <NoteCard title="Notas" value={purchaseOrder.notes} t={t} />

      {onEdit ? (
        <div style={{ marginTop: 4, display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={() => onEdit(purchaseOrder)}
            style={{
              height: 34,
              padding: '0 14px',
              borderRadius: 8,
              border: 'none',
              background: '#2563eb',
              color: '#fff',
              fontWeight: 600,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            Editar orden
          </button>
        </div>
      ) : null}
    </div>
  );

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', paddingRight: 8 }}>
          <span
            style={{
              fontSize: 20,
              fontWeight: 800,
              color: '#2563eb',
              letterSpacing: '-0.02em',
              lineHeight: 1.1,
            }}
          >
            {purchaseOrder.po_code || '—'}
          </span>
          <span
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: isDarkMode ? '#a1a1aa' : '#64748b',
            }}
          >
            {formatDate(purchaseOrder.order_date)}
          </span>
          <span
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: isDarkMode ? '#d4d4d8' : '#334155',
            }}
          >
            {purchaseOrder.buyer_name || purchaseOrder.created_by || 'Sin ejecutivo'}
          </span>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '3px 10px',
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 700,
              background: st.bg,
              color: st.color,
              border: `1px solid ${st.border}`,
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: st.bar,
                flexShrink: 0,
              }}
            />
            {st.label}
          </span>
        </div>
      }
      subtitle=""
      isDarkMode={isDarkMode}
      size="xxl"
    >
      {body}
    </Dialog>
  );
};

export default PurchaseOrderDetailModal;
